import type { FeedbackCategory, MessageReaction } from '@nile/ai-core/feedback'
import { baseApi } from './baseQuery'

/** Desired final feedback state for an assistant message. `reaction: null` clears it. */
export interface SubmitMessageFeedbackRequest {
    messageId: string
    reaction: MessageReaction | null
    /** Thumbs-down reason. Omit to leave the stored value alone; null to clear it. */
    category?: FeedbackCategory | null
    /** Free-text detail. Omit to leave the stored value alone; null to clear it. */
    comment?: string | null
}

export interface MessageFeedbackResponse {
    message?: {
        id?: string
        user_reaction?: MessageReaction | null
        feedback_category?: FeedbackCategory | null
        feedback_comment?: string | null
    }
}

/**
 * The chat route posts a finished assistant turn to Nile-Core from a `waitUntil` task, so that write
 * only starts once the stream has closed to the browser. A rating sent immediately after the last
 * token can therefore beat it and come back 404, which one short retry is enough to absorb.
 */
const MESSAGE_NOT_YET_PERSISTED_RETRY_DELAY_MS = 500

const isNotFoundError = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 404

const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds))

export const assistantFeedbackApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // Targets the app's own route handler rather than Nile-Core, so it can also score the
        // Braintrust span server-side. No invalidation: the thread is owned by useChat, not RTK Query.
        submitMessageFeedback: builder.mutation<MessageFeedbackResponse, SubmitMessageFeedbackRequest>({
            queryFn: async ({ messageId, reaction, category, comment }, _api, _extraOptions, baseQuery) => {
                const request = {
                    url: '/api/chat/feedback',
                    method: 'PATCH',
                    body: {
                        messageId,
                        reaction,
                        ...(category !== undefined ? { category } : {}),
                        ...(comment !== undefined ? { comment } : {}),
                    },
                }

                let result = await baseQuery(request)
                if (isNotFoundError(result.error)) {
                    await delay(MESSAGE_NOT_YET_PERSISTED_RETRY_DELAY_MS)
                    result = await baseQuery(request)
                }
                return result as { data: MessageFeedbackResponse } | { error: unknown }
            },
        }),
    }),
    overrideExisting: false,
})

export const { useSubmitMessageFeedbackMutation } = assistantFeedbackApi
